import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../styles/page-titles.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import { FiSearch, FiPlusCircle, FiPlus, FiPrinter, FiCopy, FiCheck } from "react-icons/fi";
import { IoGridOutline } from "react-icons/io5";
import { AuthContext } from "../../contexts/auth";
import styled from "styled-components";
import { getStoredUser } from '../../utils/safeStorage';
import { toast } from "react-toastify";
import { SidebarContext } from "../../AppRoutes";
import { useTranslation } from 'react-i18next';
import io from 'socket.io-client';
import getApiUrl from "../../utils/getApiUrl";

import PermissionGate from "../../services/PermissionGate";

import { FaChevronDown, FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { RiSubtractLine } from "react-icons/ri";
import { AiOutlineQrcode } from "react-icons/ai";
import { RxDotsVertical } from "react-icons/rx";

import { createMesa, createManyMesa, deleteMesa, renameMesa, imprimirPedidoMesa, imprimirResumoMesa } from "../../services/api";
import useGetMesas from "../../hooks/Mesas/useGetMesas";
import CustomModal from "../../components/CustomModal";
import CustomDrawer from "../../components/CustomDrawer";
import PedidosComponent from "../../components/PedidosComponent";
import { formatPrice, formatPriceWithSymbol } from "../../utils/currency";
import ModalPedido from "../../components/ModalPedido";
import ModalLinkCardapioSalao from "../../components/ModalLinkCardapioSalao";
import ModalCloseAccount from "../../components/ModalCloseAccount";
import regionConfig from "../../config/regionConfig";

/* ═══════════════════════════════════════════════════════════════
   STYLED COMPONENTS — Scoped with "mesas-" prefix
   All styles are self-contained to prevent CSS conflicts.
   ═══════════════════════════════════════════════════════════════ */

/* Wrapper principal — cuida apenas do margin-left do sidebar (padrão PageWrapper) */
const MesasContainer = styled.div`
  display: flex;
  margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
  min-height: 100vh;
  width: auto;
  transition: margin-left 200ms ease;
  background: #ffffff;
  overflow: initial;
  z-index: 9;

  @media (max-width: 880px) {
    margin-left: 0;
  }

  /* ── Toolbar (search + legend + sort) ── */
  .mesas-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }

  .mesas-toolbar-left {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
  }

  /* ── Search Input ── */
  .mesas-search {
    position: relative;
    width: 100%;
    max-width: 300px;
    min-width: 200px;
  }

  .mesas-search input {
    width: 100%;
    height: 42px;
    padding: 0 16px 0 42px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.9rem;
    color: #334155;
    background: #fff;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .mesas-search input:focus {
    border-color: rgb(49, 140, 213);
    box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.1);
  }

  .mesas-search input::placeholder {
    color: #94a3b8;
  }

  .mesas-search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 16px;
    pointer-events: none;
  }

  /* ── Status Legend ── */
  .mesas-legend {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
  }

  .mesas-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    color: #64748b;
    white-space: nowrap;
  }

  .mesas-legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
  }

  .mesas-legend-dot--free { background: #5aaf51; }
  .mesas-legend-dot--occupied { background: #ff876c; }
  .mesas-legend-dot--pending { background: #ffc555; }

  /* ── Sort Controls ── */
  .mesas-sort {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  .mesas-sort-option {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.85rem;
    color: #64748b;
    cursor: pointer;
    border: 1.5px solid #e2e8f0;
    background: #fff;
    transition: all 0.2s;
    white-space: nowrap;
    user-select: none;
  }

  .mesas-sort-option:hover {
    border-color: rgba(49, 140, 213, 0.4);
    color: rgb(49, 140, 213);
  }

  .mesas-sort-option input {
    accent-color: rgb(49, 140, 213);
    margin: 0;
    cursor: pointer;
  }

  .mesas-sort-option--active {
    border-color: rgb(49, 140, 213);
    background: rgba(49, 140, 213, 0.06);
    color: rgb(49, 140, 213);
  }

  /* ── Waiter Info Banner ── */
  .mesas-waiter-info {
    display: flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, rgba(49, 140, 213, 0.08), rgba(79, 172, 254, 0.06));
    border: 1px solid rgba(49, 140, 213, 0.2);
    border-radius: 10px;
    padding: 12px 18px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .mesas-waiter-info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(49, 140, 213, 0.12);
    color: rgb(49, 140, 213);
    font-size: 16px;
    flex-shrink: 0;
  }

  .mesas-waiter-info-text {
    font-size: 0.88rem;
    color: #475569;
    flex: 1;
    min-width: 0;
  }

  .mesas-waiter-info-link {
    font-weight: 600;
    color: rgb(49, 140, 213);
    text-decoration: none;
    word-break: break-all;
  }

  .mesas-waiter-info-link:hover {
    text-decoration: underline;
  }

  .mesas-waiter-info-copy {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 6px;
    border: 1px solid rgba(49, 140, 213, 0.3);
    background: #fff;
    color: rgb(49, 140, 213);
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
    white-space: nowrap;
  }

  .mesas-waiter-info-copy:hover {
    background: rgba(49, 140, 213, 0.06);
    border-color: rgb(49, 140, 213);
  }

  .mesas-waiter-info-copy--copied {
    color: #22c55e;
    border-color: rgba(34, 197, 94, 0.3);
  }

  .mesas-waiter-info-copy--copied:hover {
    background: rgba(34, 197, 94, 0.06);
    border-color: #22c55e;
  }

  @media (max-width: 480px) {
    .mesas-waiter-info {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
    .mesas-waiter-info-copy {
      align-self: flex-end;
    }
  }

  /* ── Cards Grid ── */
  .mesas-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
  }

  /* ── Mesa Card ── */
  .mesas-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    overflow: visible;
    transition: border-color 0.2s, box-shadow 0.2s;
    cursor: pointer;
    position: relative;
  }

  .mesas-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  .mesas-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    gap: 12px;
  }

  .mesas-card-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: #334155;
    margin: 0;
  }

  .mesas-card-actions {
    display: flex;
    gap: 6px;
    position: relative;
    flex-shrink: 0;
  }

  .mesas-card-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 6px 14px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #fff;
    background: rgb(49, 140, 213);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
  }

  .mesas-card-btn:hover {
    background: rgb(79, 172, 254);
  }

  .mesas-card-btn--icon {
    padding: 6px 10px;
  }

  .mesas-card-status {
    text-align: center;
    padding: 6px 0;
    font-size: 0.82rem;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.02em;
    border-radius: 0 0 10px 10px;
  }

  /* ── Dropdown Menu ── */
  .mesas-dropdown {
    position: absolute;
    z-index: 20;
    top: calc(100% + 6px);
    right: 0;
    width: 200px;
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .mesas-dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    font-size: 0.875rem;
    color: #475569;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    border-bottom: 1px solid #f1f5f9;
  }

  .mesas-dropdown-item:last-child {
    border-bottom: none;
  }

  .mesas-dropdown-item:hover {
    background: rgb(49, 140, 213);
    color: #fff;
  }

  .mesas-dropdown-item svg {
    font-size: 14px;
    flex-shrink: 0;
  }

  /* ── Create Mesa Buttons ── */
  .mesas-create-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
    margin-top: 8px;
  }

  .mesas-create-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 28px 20px;
    background: #fff;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
  }

  .mesas-create-card:hover {
    border-color: rgb(49, 140, 213);
    background: rgba(49, 140, 213, 0.02);
  }

  .mesas-create-card.disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }

  .mesas-create-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.95rem;
    font-weight: 500;
    color: rgb(49, 140, 213);
    margin: 0;
  }

  .mesas-create-subtitle {
    font-size: 0.82rem;
    color: #94a3b8;
    margin: 0;
  }

  /* ── Detail View (Occupied Table) ── */
  .mesas-detail-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }

  .mesas-btn-outline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 20px;
    font-size: 0.9rem;
    font-weight: 500;
    color: rgb(49, 140, 213);
    background: #fff;
    border: 1.5px solid rgb(49, 140, 213);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .mesas-btn-outline:hover {
    background: rgb(49, 140, 213);
    color: #fff;
  }

  .mesas-btn-outline--full {
    width: 100%;
    justify-content: center;
  }

  .mesas-btn-outline--grow {
    flex: 1;
    justify-content: center;
  }

  /* ── Orders Table ── */
  .mesas-orders-container {
    width: 100%;
    background: #fff;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    overflow: hidden;
  }

  .mesas-orders-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    padding: 16px 20px;
    margin: 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .mesas-orders-scroll {
    max-height: 50vh;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  .mesas-orders-scroll::-webkit-scrollbar {
    width: 4px;
  }

  .mesas-orders-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  .mesas-order-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    background: rgb(49, 140, 213);
    color: #fff;
    font-size: 0.875rem;
  }

  .mesas-order-header p {
    margin: 0;
  }

  .mesas-order-header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .mesas-order-print {
    cursor: pointer;
    display: flex;
    align-items: center;
    opacity: 0.85;
    transition: opacity 0.15s;
  }

  .mesas-order-print:hover {
    opacity: 1;
  }

  .mesas-order-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    font-size: 0.85rem;
    color: #475569;
    border-bottom: 1px solid #f8fafc;
  }

  .mesas-order-item p {
    margin: 0;
  }

  .mesas-order-item-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mesas-order-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    background: rgb(49, 140, 213);
    border-radius: 6px;
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    transition: background 0.15s;
  }

  .mesas-order-dots:hover {
    background: rgb(79, 172, 254);
  }

  .mesas-order-adicional {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 20px 6px 44px;
    font-size: 0.8rem;
    color: #64748b;
  }

  .mesas-order-adicional p {
    margin: 0;
  }

  .mesas-order-subtotal {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    border-top: 1px solid #e2e8f0;
    font-weight: 600;
    font-size: 0.9rem;
    color: #334155;
  }

  .mesas-order-subtotal p {
    margin: 0;
  }

  /* ── Footer Totals ── */
  .mesas-orders-footer {
    border-top: 2px solid #e2e8f0;
    padding: 12px 20px;
    background: #fafbfc;
  }

  .mesas-orders-footer-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 0;
    font-size: 0.95rem;
    color: #334155;
  }

  .mesas-orders-footer-row h4 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .mesas-orders-footer-row p {
    margin: 0;
    font-weight: 600;
  }

  /* ── Close Account Modal Styles ── */
  .mesas-close-account {
    padding: 8px 0;
  }

  .mesas-close-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1e293b;
    background: #f8fafc;
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .mesas-close-total p {
    margin: 0;
  }

  .mesas-close-split {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    font-size: 0.9rem;
    color: #475569;
  }

  .mesas-close-split p {
    margin: 0;
  }

  .mesas-close-split-controls {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mesas-close-split-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    font-size: 1.2rem;
    color: #475569;
    cursor: pointer;
    transition: all 0.15s;
  }

  .mesas-close-split-btn:hover {
    border-color: rgb(49, 140, 213);
    color: rgb(49, 140, 213);
  }

  .mesas-close-split-count {
    font-size: 1rem;
    font-weight: 600;
    min-width: 24px;
    text-align: center;
  }

  .mesas-payment-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 16px 0;
    border-top: 1px solid #f1f5f9;
    margin-top: 8px;
  }

  .mesas-payment-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 14px 16px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #fff;
    background: rgb(49, 140, 213);
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .mesas-payment-btn:hover {
    background: rgb(79, 172, 254);
  }

  .mesas-payment-btn svg {
    font-size: 20px;
  }

  .mesas-payment-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 8px;
  }

  .mesas-payment-form label {
    font-size: 0.85rem;
    font-weight: 500;
    color: #475569;
    margin-bottom: 4px;
  }

  .mesas-payment-submit {
    width: 100%;
    padding: 12px;
    font-size: 0.95rem;
    font-weight: 600;
    color: #fff;
    background: rgb(49, 140, 213);
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .mesas-payment-submit:hover {
    background: rgb(79, 172, 254);
  }

  .mesas-payment-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Counter styles moved to MesasModalWrapper (rendered outside MesasContainer) */

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .mesas-toolbar {
      flex-direction: column;
      align-items: stretch;
    }

    .mesas-toolbar-left {
      flex-direction: column;
      align-items: stretch;
    }

    .mesas-search {
      max-width: 100%;
    }

    .mesas-legend {
      justify-content: flex-start;
    }

    .mesas-sort {
      justify-content: flex-start;
    }

    .mesas-grid {
      grid-template-columns: 1fr;
    }

    .mesas-create-row {
      grid-template-columns: 1fr;
    }

    .mesas-detail-actions {
      flex-direction: column;
    }

    .mesas-btn-outline {
      width: 100%;
      justify-content: center;
    }
  }

  @media (max-width: 480px) {
    .mesas-card-header {
      padding: 12px 14px;
      flex-wrap: wrap;
    }

    .mesas-card-btn {
      padding: 5px 10px;
      font-size: 0.75rem;
    }

    .mesas-payment-buttons {
      grid-template-columns: 1fr;
    }
  }
`;

/* Wrapper for modal counter styles — rendered outside MesasContainer */
const MesasModalWrapper = styled.div`
  .mesas-create-many-counter {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 0;
    margin: 4px 0;
  }

  .mesas-counter-wrap {
    display: inline-flex;
    align-items: center;
    background: #fff;
    border: 2px solid #d1d5db;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: border-color 0.2s;
  }

  .mesas-counter-wrap:focus-within {
    border-color: rgb(49, 140, 213);
    box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.12);
  }

  .mesas-counter-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border: none;
    background: transparent;
    color: #4b5563;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .mesas-counter-btn:hover:not(:disabled) {
    background: #f3f4f6;
    color: rgb(49, 140, 213);
  }

  .mesas-counter-btn:active:not(:disabled) {
    background: #e5e7eb;
    transform: scale(0.93);
  }

  .mesas-counter-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .mesas-counter-value {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 56px;
    height: 48px;
    padding: 0 4px;
    border-left: 1.5px solid #e5e7eb;
    border-right: 1.5px solid #e5e7eb;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    user-select: none;
    background: #f9fafb;
  }

  .mesas-counter-input {
    width: 72px;
    height: 48px;
    padding: 0;
    border: none;
    border-left: 1.5px solid #e5e7eb;
    border-right: 1.5px solid #e5e7eb;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    text-align: center;
    outline: none;
    background: #f9fafb;
    transition: background 0.2s;
    -moz-appearance: textfield;
  }

  .mesas-counter-input::-webkit-outer-spin-button,
  .mesas-counter-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .mesas-counter-input:focus {
    background: #fff;
  }

  .mesas-create-many-info {
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0;
    text-align: center;
  }
`;

/* ═══════════════════════════════════════════════════════════════
   STATUS COLORS — Shared constant
   ═══════════════════════════════════════════════════════════════ */

const STATUS_COLORS = {
  available: "#5aaf51",
  free: "#5aaf51",
  occupied: "#ff876c",
  pending: "#ffc555"
};

/* ═══════════════════════════════════════════════════════════════
   MESAS COMPONENT
   ═══════════════════════════════════════════════════════════════ */

function Mesas() {
  const { t } = useTranslation('mesas');

  const empresa = localStorage.getItem('empresa');
  const empresaParse = JSON.parse(empresa);
  const idEmpresa = empresaParse.id_empresa;
  const nomeEmpresa = empresaParse.name;
  const nomeEmpresaForUrl = nomeEmpresa
    .replace(/\s+/g, "-")
    .replace(/[A-Z]/g, (c) => c.toLowerCase());

  const [dropdownOpened, setDropdownOpened] = useState('');
  const [isCreatingMesa, setIsCreatingMesa] = useState(false);
  const [isCreatingManyMesa, setIsCreatingManyMesa] = useState(false);
  const [currentDelMesa, setCurrentDelMesa] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isPedidoDrawerOpen, setIsPedidoDrawerOpen] = useState(false);
  const [currentPedidoDrawer, setCurrentPedidoDrawer] = useState('');
  const [currentMesaEdit, setCurrentMesaEdit] = useState(null);
  const [editPedido, setEditPedido] = useState(null);
  const [isCloseAccount, setIsCloseAccount] = useState(false);
  const [showQRCodeSalao, setShowQRCodeSalao] = useState(false);
  const [currentRenameMesa, setCurrentRenameMesa] = useState(null);
  const [renameNumber, setRenameNumber] = useState(1);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isPrintingResumo, setIsPrintingResumo] = useState(false);

  const userParse = getStoredUser();
  const userID = userParse?._id;

  const [count, setCount] = useState(1);
  const [newMesaNumber, setNewMesaNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { sidebar } = useContext(SidebarContext);
  const [tab, setTab] = useState('');
  const [sortBy, setSortBy] = useState('table');
  const [waiterLinkCopied, setWaiterLinkCopied] = useState(false);

  const garcomLink = `https://${regionConfig.brand.garcomDomain}`;

  const { data: mesas, refetch } = useGetMesas();
  const { user } = useContext(AuthContext);

  const hashMapMesas = useMemo(() => new Map(mesas?.map(mesa => [mesa._id, mesa])), [mesas]);

  const sortedMesas = useMemo(() => {
    if (!mesas) return [];
    const mesasCopy = [...mesas];

    if (sortBy === 'table') {
      // Sort by mesa number (simple ID) ascending
      return mesasCopy.sort((a, b) => {
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      });
    }

    if (sortBy === 'status') {
      // Sort by status: occupied first, then pending, then free
      const statusOrder = { occupied: 0, pending: 1, free: 2, available: 2 };
      return mesasCopy.sort((a, b) => {
        const orderA = statusOrder[a.status] ?? 3;
        const orderB = statusOrder[b.status] ?? 3;
        if (orderA !== orderB) return orderA - orderB;
        // Same status → sort by number
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      });
    }

    return mesasCopy;
  }, [mesas, sortBy]);

  const filteredMesas = useMemo(() => {
    if (!searchTerm.trim()) return sortedMesas;
    const term = searchTerm.trim().toLowerCase();
    return sortedMesas.filter(mesa =>
      mesa.name?.toLowerCase().includes(term) ||
      String(mesa.number).includes(term)
    );
  }, [sortedMesas, searchTerm]);

  const getStatusDescription = useCallback((status) => {
    const map = {
      available: t('status.free'),
      free: t('status.free'),
      occupied: t('status.occupied'),
      pending: t('status.pending')
    };
    return map[status] || status;
  }, [t]);

  /* ── Event Handlers ── */

  const handleCopyWaiterLink = useCallback(() => {
    navigator.clipboard.writeText(garcomLink).then(() => {
      setWaiterLinkCopied(true);
      setTimeout(() => setWaiterLinkCopied(false), 2000);
    });
  }, [garcomLink]);

  const handleOpenCreateSingleMesa = () => {
    let bigger = 0;
    mesas && mesas.forEach((mesa) => {
      if (Number(mesa.number) > bigger) bigger = Number(mesa.number);
    });
    setNewMesaNumber(bigger + 1);
    setIsOpen(true);
    setTab('createSingle');
  };

  const handleCreateMesa = async (mesaNumber) => {
    try {
      setIsCreatingMesa(true);
      const name = t('tableName', { number: mesaNumber });
      const qr_code = '';

      await createMesa({ name, qr_code, number: mesaNumber });
      handleCancel();
      refetch();
      toast(t('toasts.createSuccess'), { autoClose: 5000, type: "success" });
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao criar mesa', error);
      if (error?.response?.status === 422) {
        toast(t('toasts.duplicateError', { number: mesaNumber }), { autoClose: 5000, type: "error" });
      } else {
        toast(t('toasts.createError'), { autoClose: 5000, type: "error" });
      }
    } finally {
      setIsCreatingMesa(false);
    }
  };

  const handleManyCreateMesas = async () => {
    try {
      setIsCreatingManyMesa(true);
      const hostname = window.location.hostname === 'localhost' ? 'http://localhost:3000' : `https://${window.location.hostname}`;
      const url = hostname + '/cardapio/' + nomeEmpresaForUrl + '/' + idEmpresa;

      // Extract prefix from tableName (e.g. "Table" from "Table {{number}}" or "Mesa" from "Mesa {{number}}")
      const namePrefix = t('tableName', { number: '' }).trim();
      await createManyMesa({ url, quantity: count, namePrefix });
      setIsOpen(false);
      setTab('');
      refetch();
      toast(t('toasts.createManySuccess'), { autoClose: 5000, type: "success" });
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao criar mesa', error);
      toast(t('toasts.createError'), { autoClose: 5000, type: "error" });
    } finally {
      setIsCreatingManyMesa(false);
    }
  };

  useEffect(() => {
    const closeDropdown = () => setDropdownOpened('');
    document.addEventListener('click', closeDropdown);
    return () => document.removeEventListener('click', closeDropdown);
  }, []);

  // Socket.IO: ouvir atualizações de mesa em tempo real (itens adicionados de outro app/tela)
  const socketRef = useRef(null);
  useEffect(() => {
    const API_URL = getApiUrl();
    const userEmpresa = JSON.parse(localStorage.getItem('userEmpresa') || '{}');
    const idEmpresa = userEmpresa?.id;
    if (!idEmpresa) return;

    const socket = io(API_URL, {
      withCredentials: true,
      transports: ['websocket'],
      auth: { token: localStorage.getItem('token') },
      reconnection: true,
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('joinCompanyRoom', { companyId: idEmpresa.toString() });
    });

    // Quando itens são adicionados/atualizados em uma mesa por outra tela
    socket.on('mesa_atualizada', () => {
      refetch().then((result) => {
        if (result?.data && currentMesaEdit) {
          const mesaAtualizada = result.data.find(m => m._id === currentMesaEdit._id);
          if (mesaAtualizada) setCurrentMesaEdit(mesaAtualizada);
        }
      });
    });

    // Quando um novo pedido é criado (garcom app, cardápio digital)
    socket.on('novoPedido', () => {
      refetch().then((result) => {
        if (result?.data && currentMesaEdit) {
          const mesaAtualizada = result.data.find(m => m._id === currentMesaEdit._id);
          if (mesaAtualizada) setCurrentMesaEdit(mesaAtualizada);
        }
      });
    });

    return () => {
      socket.off('mesa_atualizada');
      socket.off('novoPedido');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [currentMesaEdit?._id]);

  const handleOpenDropDown = (event, mesaId) => {
    setDropdownOpened(dropdownOpened !== mesaId ? mesaId : '');
    event.stopPropagation();
  };

  const handleOpenPedidoDrawer = (mesaId) => {
    setIsPedidoDrawerOpen(true);
    setCurrentPedidoDrawer(mesaId);
  };

  // Buscar dados atualizados da mesa antes de abrir o modal de fechamento
  const handleOpenCloseAccount = async () => {
    try {
      const result = await refetch();
      if (result?.data && currentMesaEdit) {
        const mesaAtualizada = result.data.find(m => m._id === currentMesaEdit._id);
        if (mesaAtualizada) {
          setCurrentMesaEdit(mesaAtualizada);
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar dados da mesa:', error);
    }
    setIsCloseAccount(true);
  };

  const handleCancel = () => {
    setTab('');
    setIsOpen(false);
  };

  const handleDeleteMesa = async () => {
    try {
      await deleteMesa({ mesaId: currentDelMesa._id });
      refetch();
      toast(t('toasts.deleteSuccess'), { autoClose: 5000, type: "success" });
      setIsOpen(false);
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao deletar mesa', error);
      toast(t('toasts.deleteError'), { autoClose: 5000, type: "error" });
    }
  };

  const handleOpenDelete = (mesa) => {
    setCurrentDelMesa(mesa);
    setIsOpen(true);
    setTab('delete');
  };

  const handleOpenQRCode = (mesa) => {
    setShowQRCodeSalao(true);
  };

  const handleOpenCreateManyMesas = () => {
    setIsOpen(true);
    setTab('manyMesas');
  };

  const handleOpenRenameMesa = (mesa) => {
    setCurrentRenameMesa(mesa);
    setRenameNumber(mesa.number || 1);
    setIsOpen(true);
    setTab('renameMesa');
  };

  const handleRenameMesa = async () => {
    if (!currentRenameMesa || !renameNumber) return;
    try {
      setIsRenaming(true);
      const newName = t('tableName', { number: renameNumber });
      await renameMesa({ mesaId: currentRenameMesa._id, number: renameNumber, name: newName });
      handleCancel();
      refetch();
      toast(t('toasts.renameSuccess'), { autoClose: 5000, type: "success" });
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao renomear mesa', error);
      if (error?.response?.status === 422) {
        toast(t('toasts.duplicateError', { number: renameNumber }), { autoClose: 5000, type: "error" });
      } else {
        toast(t('toasts.renameError'), { autoClose: 5000, type: "error" });
      }
    } finally {
      setIsRenaming(false);
    }
  };

  // Conferencia da mesa (pre-conta): total, o que ja foi pago e a gorjeta
  // sugerida sobre o SALDO. E a via que vai para o cliente decidir -- por isso
  // sai pelo backend, que e quem conhece a config de gorjeta e a cotacao.
  const handlePrintResumo = async () => {
    if (!currentMesaEdit?._id || isPrintingResumo) return;
    try {
      setIsPrintingResumo(true);
      await imprimirResumoMesa(currentMesaEdit._id);
      toast(t('toasts.summaryPrintSuccess'), { type: "success" });
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao imprimir conferencia da mesa', error);
      toast(t('toasts.summaryPrintError'), { type: "error" });
    } finally {
      setIsPrintingResumo(false);
    }
  };

  // Impressao de pedido de mesa: o payload da comanda e montado no BACKEND.
  //
  // Antes ele era montado aqui e quebrava no clique: pedido de mesa nao tem
  // `entrega`, entao `infoPedido.entrega.tipo_entrega` estourava. Fora o crash,
  // o payload local saia como pedido de delivery (sem o nome da mesa), sem
  // moeda/cotacao e no idioma do app em vez do da empresa. O /mesas/print-pedido
  // -- o mesmo endpoint que o garcomapp ja usa -- resolve os quatro de uma vez.
  const handlePrint = async (infoPedido) => {
    const pedidoId = infoPedido?._id;
    if (!pedidoId) {
      toast(t('toasts.printError'), { type: "error" });
      return;
    }
    try {
      await imprimirPedidoMesa(pedidoId);
      toast(t('toasts.printSuccess'), { type: "success" });
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao imprimir pedido de mesa', error);
      toast(t('toasts.printError'), { type: "error" });
    }
  };


  /* ── Modal Renderers ── */

  const tabs = {
    'delete': () => (
      <CustomModal
        title={t('deleteModal.title')}
        open={isOpen}
        onCancel={handleCancel}
        onOk={handleDeleteMesa}
        okText={t('deleteModal.okText')}
        cancelText={t('common:buttons.cancel')}
      >
        <div className="h-100 w-100">
          <p dangerouslySetInnerHTML={{ __html: t('deleteModal.description', { name: currentDelMesa?.name }) }} />
        </div>
      </CustomModal>
    ),
    'manyMesas': () => (
      <CustomModal
        loading={isCreatingManyMesa}
        title={t('createMany.modalTitle')}
        open={isOpen}
        onCancel={handleCancel}
        onOk={handleManyCreateMesas}
        okText={t('createMany.okText')}
        cancelText={t('common:buttons.cancel')}
      >
        <div className="h-100 w-100">
          <p>{t('createMany.question')}</p>
          <div className="mesas-create-many-counter">
            <div className="mesas-counter-wrap">
              <button className="mesas-counter-btn" disabled={count <= 1} onClick={() => setCount(prev => prev - 1)}>
                <RiSubtractLine />
              </button>
              <span className="mesas-counter-value">{count}</span>
              <button className="mesas-counter-btn" onClick={() => setCount(prev => prev + 1)}>
                <FiPlus />
              </button>
            </div>
          </div>
          <p className="mesas-create-many-info">{t('createMany.info')}</p>
        </div>
      </CustomModal>
    ),
    'createSingle': () => (
      <CustomModal
        loading={isCreatingMesa}
        title={t('createSingle.modalTitle')}
        open={isOpen}
        onCancel={handleCancel}
        onOk={() => handleCreateMesa(newMesaNumber)}
        okText={t('createSingle.okText')}
        cancelText={t('common:buttons.cancel')}
      >
        <div className="h-100 w-100">
          <p>{t('createSingle.tableNumber')}</p>
          <div className="mesas-create-many-counter">
            <div className="mesas-counter-wrap">
              <button className="mesas-counter-btn" disabled={newMesaNumber <= 1} onClick={() => setNewMesaNumber(prev => Math.max(1, prev - 1))}>
                <RiSubtractLine />
              </button>
              <input
                className="mesas-counter-input"
                type="number"
                min="1"
                value={newMesaNumber}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val > 0) setNewMesaNumber(val);
                  else if (e.target.value === '') setNewMesaNumber('');
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) setNewMesaNumber(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateMesa(newMesaNumber);
                }}
              />
              <button className="mesas-counter-btn" onClick={() => setNewMesaNumber(prev => (typeof prev === 'number' ? prev + 1 : 1))}>
                <FiPlus />
              </button>
            </div>
          </div>
          <p className="mesas-create-many-info">{t('createSingle.preview', { number: newMesaNumber })}</p>
        </div>
      </CustomModal>
    ),
    'renameMesa': () => (
      <CustomModal
        loading={isRenaming}
        title={t('rename.modalTitle')}
        open={isOpen}
        onCancel={handleCancel}
        onOk={handleRenameMesa}
        okText={t('rename.okText')}
        cancelText={t('common:buttons.cancel')}
      >
        <div className="h-100 w-100">
          <p>{t('rename.currentName', { name: currentRenameMesa?.name })}</p>
          <p style={{ marginTop: 12 }}>{t('rename.newNumber')}</p>
          <div className="mesas-create-many-counter">
            <div className="mesas-counter-wrap">
              <button className="mesas-counter-btn" disabled={renameNumber <= 1} onClick={() => setRenameNumber(prev => Math.max(1, prev - 1))}>
                <RiSubtractLine />
              </button>
              <input
                className="mesas-counter-input"
                type="number"
                min="1"
                value={renameNumber}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val > 0) setRenameNumber(val);
                  else if (e.target.value === '') setRenameNumber('');
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) setRenameNumber(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameMesa();
                }}
              />
              <button className="mesas-counter-btn" onClick={() => setRenameNumber(prev => (typeof prev === 'number' ? prev + 1 : 1))}>
                <FiPlus />
              </button>
            </div>
          </div>
          <p className="mesas-create-many-info">{t('rename.preview', { number: renameNumber })}</p>
        </div>
      </CustomModal>
    ),
  };

  /* ── Render Helpers ── */

  const renderToolbar = (showSort = false, showBackButton = false) => (
    <div className="mesas-toolbar">
      <div className="mesas-toolbar-left">
        <div className="mesas-search">
          <span className="mesas-search-icon"><FiSearch /></span>
          <input
            placeholder={t('search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="mesas-legend">
          <div className="mesas-legend-item">
            <div className="mesas-legend-dot mesas-legend-dot--free" />
            {t('status.free')}
          </div>
          <div className="mesas-legend-item">
            <div className="mesas-legend-dot mesas-legend-dot--occupied" />
            {t('status.occupied')}
          </div>
          <div className="mesas-legend-item">
            <div className="mesas-legend-dot mesas-legend-dot--pending" />
            {t('status.pending')}
          </div>
        </div>
      </div>
      {showSort && (
        <div className="mesas-sort">
          <label className={`mesas-sort-option ${sortBy === 'table' ? 'mesas-sort-option--active' : ''}`}>
            <input type="radio" name="mesasSort" checked={sortBy === 'table'} onChange={() => setSortBy('table')} />
            <span>{t('sort.byTable')}</span>
          </label>
          <label className={`mesas-sort-option ${sortBy === 'status' ? 'mesas-sort-option--active' : ''}`}>
            <input type="radio" name="mesasSort" checked={sortBy === 'status'} onChange={() => setSortBy('status')} />
            <span>{t('sort.byStatus')}</span>
          </label>
        </div>
      )}
      {showBackButton && (
        <button className="mesas-btn-outline" onClick={() => setCurrentMesaEdit(null)}>
          {t('mesaDetail.backToMap')}
        </button>
      )}
    </div>
  );

  /* ── Main Render ── */

  return (
    <>
      <PermissionGate permissions={["default"]}>
        <MesasContainer sidebar={sidebar}>
          <div className="pedeja-page-container">
            <div className="pedeja-page-content">
              <div className="page-title-with-icon">
                <div className="page-icon"><IoGridOutline /></div>
                <h1 className="page-title">{t('title')}</h1>
              </div>

              {/* ── Waiter App Info Banner ── */}
              <div className="mesas-waiter-info">
                <div className="mesas-waiter-info-icon">
                  <IoGridOutline />
                </div>
                <span className="mesas-waiter-info-text">
                  {t('waiterInfo.message')}{' '}
                  <a href={garcomLink} target="_blank" rel="noopener noreferrer" className="mesas-waiter-info-link">
                    {garcomLink}
                  </a>
                </span>
                <button
                  className={`mesas-waiter-info-copy ${waiterLinkCopied ? 'mesas-waiter-info-copy--copied' : ''}`}
                  onClick={handleCopyWaiterLink}
                >
                  {waiterLinkCopied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  {waiterLinkCopied ? t('waiterInfo.copied') : t('waiterInfo.copy')}
                </button>
              </div>

            {currentMesaEdit ? (
              <>
                {renderToolbar(false, true)}

                <div className="mesas-detail-actions">
                  <button className="mesas-btn-outline mesas-btn-outline--grow" onClick={handleOpenCloseAccount}>
                    {t('mesaDetail.closeAccount')}
                  </button>
                  <button
                    className="mesas-btn-outline mesas-btn-outline--grow"
                    onClick={handlePrintResumo}
                    disabled={isPrintingResumo}
                  >
                    <FiPrinter />
                    {t('mesaDetail.printSummary')}
                  </button>
                </div>

                <div className="mesas-orders-container">
                  <h2 className="mesas-orders-title">{currentMesaEdit.name}</h2>
                  <div className="mesas-orders-scroll">
                    {currentMesaEdit.pedidos && currentMesaEdit.pedidos.filter(item => item.id_pedido && item.id_pedido.itens).map((item) => (
                      <div key={item._id}>
                        <div className="mesas-order-header">
                          <p>{t('mesaDetail.order', { number: item.id_pedido.id_pedido_counter })}</p>
                          <div className="mesas-order-header-right">
                            <p>{item.id_pedido.nome_cliente}</p>
                            <div className="mesas-order-print" onClick={() => handlePrint(item.id_pedido)}>
                              <FiPrinter />
                            </div>
                          </div>
                        </div>
                        {item.id_pedido.itens.map((item_pedido, idx) => (
                          <React.Fragment key={idx}>
                            <div className="mesas-order-item">
                              <p>{item_pedido.quantidade}x {item_pedido.nomeItem}</p>
                              <div className="mesas-order-item-right">
                                <p>{formatPriceWithSymbol(item_pedido.valor)}</p>
                                <div className="mesas-order-dots" onClick={() => setEditPedido(item.id_pedido)}>
                                  <RxDotsVertical />
                                </div>
                              </div>
                            </div>
                            {(item_pedido.grupo_adicionais || []).map((grupoAdicionais) => (
                              <div key={grupoAdicionais.grupoAdicionais}>
                                {(grupoAdicionais.adicionais || []).map((adicionais, aidx) => (
                                  <div key={aidx} className="mesas-order-adicional">
                                    <p>{adicionais.quantity}x {adicionais.title}</p>
                                    <p>{formatPriceWithSymbol(adicionais.price)}</p>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </React.Fragment>
                        ))}
                        <div className="mesas-order-subtotal">
                          <p>{t('mesaDetail.subtotal')}</p>
                          <p>{formatPriceWithSymbol(item.id_pedido.valor_total)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mesas-orders-footer">
                    <div className="mesas-orders-footer-row">
                      <h4>{t('mesaDetail.subtotal')}</h4>
                      <p>{formatPriceWithSymbol(currentMesaEdit?.pedidos?.filter(item => item.id_pedido)?.reduce((acc, item) => acc + (item.id_pedido.valor_total || 0), 0))}</p>
                    </div>
                    <div className="mesas-orders-footer-row">
                      <h4>{t('mesaDetail.totalValue')}</h4>
                      <p>{formatPriceWithSymbol(currentMesaEdit?.pedidos?.filter(item => item.id_pedido)?.reduce((acc, item) => acc + (item.id_pedido.valor_total || 0), 0))}</p>
                    </div>
                  </div>
                </div>

                {editPedido && (
                  <ModalPedido
                    setShowPedido={(showPedido) => setEditPedido(!showPedido)}
                    showPedido={!editPedido}
                    setRefresh={() => {
                      refetch().then((result) => {
                        if (result?.data && currentMesaEdit) {
                          const mesaAtualizada = result.data.find(m => m._id === currentMesaEdit._id);
                          if (mesaAtualizada) setCurrentMesaEdit(mesaAtualizada);
                        }
                      });
                    }}
                    infoPedido={editPedido}
                    setInfoPedido={() => { }}
                    handleAvancar={() => { }}
                    userID={userID}
                    hasCancelPassword={false}
                    typePedido="mesa"
                  />
                )}
              </>
            ) : (
              <>
                {renderToolbar(true, false)}

                <div className="mesas-grid">
                  {filteredMesas && filteredMesas.map((mesa) => (
                    <div
                      onClick={() => mesa.status !== 'free' && setCurrentMesaEdit(mesa)}
                      className="mesas-card"
                      style={{ cursor: mesa.status !== 'free' ? "pointer" : "default" }}
                      key={mesa._id}
                    >
                      <div className="mesas-card-header">
                        <p className="mesas-card-name">{mesa.name}</p>
                        <div className="mesas-card-actions">
                          <button className="mesas-card-btn" onClick={(e) => { e.stopPropagation(); handleOpenPedidoDrawer(mesa._id); }}>
                            {t('card.newOrder')}
                          </button>
                          <button className="mesas-card-btn mesas-card-btn--icon" onClick={(e) => { e.stopPropagation(); handleOpenDropDown(e, mesa._id); }}>
                            <FaChevronDown />
                          </button>
                          {dropdownOpened === mesa._id && (
                            <div className="mesas-dropdown">
                              {mesa.status === 'free' && (
                                <div className="mesas-dropdown-item" onClick={() => handleOpenRenameMesa(mesa)}>
                                  <FaPencilAlt />
                                  {t('dropdown.rename')}
                                </div>
                              )}
                              <div className="mesas-dropdown-item" onClick={() => handleOpenDelete(mesa)}>
                                <FaRegTrashAlt />
                                {t('dropdown.delete')}
                              </div>
                              {mesa.status !== 'free' && (
                                <div className="mesas-dropdown-item" onClick={() => setCurrentMesaEdit(mesa)}>
                                  <FaPencilAlt />
                                  {t('dropdown.edit')}
                                </div>
                              )}
                              <div className="mesas-dropdown-item" onClick={() => handleOpenQRCode(mesa)}>
                                <AiOutlineQrcode />
                                {t('dropdown.qrCode')}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className="mesas-card-status"
                        style={{ backgroundColor: STATUS_COLORS[mesa.status] }}
                      >
                        {getStatusDescription(mesa.status)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mesas-create-row">
                  <div
                    className={`mesas-create-card ${isCreatingMesa ? 'disabled' : ''}`}
                    onClick={() => !isCreatingMesa && handleOpenCreateSingleMesa()}
                  >
                    <p className="mesas-create-title">
                      <FiPlusCircle />
                      {t('createSingle.title')}
                    </p>
                    <p className="mesas-create-subtitle">{t('createSingle.subtitle')}</p>
                  </div>
                  <div
                    className={`mesas-create-card ${isCreatingMesa ? 'disabled' : ''}`}
                    onClick={() => !isCreatingMesa && handleOpenCreateManyMesas()}
                  >
                    <p className="mesas-create-title">
                      <FiPlusCircle />
                      {t('createMany.title')}
                    </p>
                    <p className="mesas-create-subtitle">{t('createMany.subtitle')}</p>
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
        </MesasContainer>

        <MesasModalWrapper>
          {tab && tabs[tab]()}
        </MesasModalWrapper>

        <CustomDrawer
          open={isPedidoDrawerOpen}
          placement="right"
          width="85vw"
          maxWidth="1200px"
          onClose={() => setIsPedidoDrawerOpen(false)}
        >
          <PedidosComponent
            mesaId={currentPedidoDrawer}
            mesaName={hashMapMesas?.get(currentPedidoDrawer)?.name || ''}
            mesaClientes={hashMapMesas?.get(currentPedidoDrawer)?.clientes || []}
            onFinish={() => { setIsPedidoDrawerOpen(false); refetch(); }}
            onClose={() => setIsPedidoDrawerOpen(false)}
          />
        </CustomDrawer>

        <ModalCloseAccount
          open={isCloseAccount}
          mesa={currentMesaEdit}
          onClose={() => setIsCloseAccount(false)}
          onPaymentSuccess={(mesaUpdated) => {
            if (mesaUpdated.status === 'free') {
              setCurrentMesaEdit(null);
              setIsCloseAccount(false);
              refetch();
            } else {
              setCurrentMesaEdit(prev => ({
                ...prev,
                total_payed: mesaUpdated.total_payed,
                itens_pagos: mesaUpdated.itens_pagos || [],
                desconto: mesaUpdated.desconto ?? prev?.desconto ?? 0,
                status: mesaUpdated.status,
              }));
            }
          }}
          refetch={refetch}
        />
      </PermissionGate>

      {showQRCodeSalao && (
        <ModalLinkCardapioSalao
          showLinkCardapioSalao={showQRCodeSalao}
          setShowLinkCardapioSalao={setShowQRCodeSalao}
          nomeEmpresaForUrl={nomeEmpresaForUrl}
        />
      )}
    </>
  );
}

export default Mesas;
